import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Store, Plus, Pencil, Trash2, Search, Ban } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { PuntoVendita } from '../types';
import WeatherWidget from '../components/WeatherWidget';
import MapSelector from '../components/MapSelector';
import {
  PageContainer,
  PageHeader,
  Button,
  Card,
  CardBody,
  Alert,
  Badge,
  LoadingState,
  Modal,
  FormGroup,
  FormActions,
  Label,
  Input,
  Select,
  Textarea,
  useConfirm,
} from '../components/ui';

const Filters = styled.div`
  margin-bottom: var(--space-xl);

  input {
    width: 100%;
    max-width: 20rem;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: var(--space-lg);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);

  h3 {
    margin: 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
  }
`;

const Indirizzo = styled.p`
  margin: 0 0 var(--space-md);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
`;

const WeatherSection = styled.div`
  margin-bottom: var(--space-md);
`;

const ClosedDay = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  color: var(--error);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  margin-bottom: var(--space-sm);
`;

const CreatedDate = styled.small`
  display: block;
  color: var(--text-muted);
  font-size: var(--font-size-xs);
  margin-bottom: var(--space-md);
`;

const CardActions = styled.div`
  display: flex;
  gap: var(--space-sm);
`;

const GeocodingBox = styled.div`
  margin-top: var(--space-sm);
  padding: var(--space-sm);
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--gray-700);
`;

interface PuntoVenditaFormData {
  nome: string;
  indirizzo: string;
  latitudine?: number | null;
  longitudine?: number | null;
  costo_vendita_kg: number;
  giorno_chiusura?: number | null;
  attivo: boolean;
}

const PuntiVenditaPage: React.FC = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPuntoVendita, setEditingPuntoVendita] = useState<PuntoVendita | null>(null);
  const [formData, setFormData] = useState<PuntoVenditaFormData>({
    nome: '',
    indirizzo: '',
    latitudine: null,
    longitudine: null,
    costo_vendita_kg: 30,
    giorno_chiusura: null,
    attivo: true
  });
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [geocodingMessage, setGeocodingMessage] = useState<{ text: string; variant: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadPuntiVendita();
  }, []);

  const loadPuntiVendita = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPuntiVendita();
      setPuntiVendita(response);
      setError(null);
    } catch (err) {
      setError('Errore nel caricamento dei punti vendita');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingPuntoVendita) {
        const puntoVenditaAggiornato = await apiService.updatePuntoVendita(editingPuntoVendita.id, formData);
        setPuntiVendita(prevPuntiVendita =>
          prevPuntiVendita.map(pv =>
            pv.id === puntoVenditaAggiornato.id ? puntoVenditaAggiornato : pv
          )
        );
      } else {
        const nuovoPuntoVendita = await apiService.createPuntoVendita(formData);
        setPuntiVendita(prevPuntiVendita => [...prevPuntiVendita, nuovoPuntoVendita]);
      }

      setShowModal(false);
      setEditingPuntoVendita(null);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Errore nel salvare il punto vendita');
    }
  };

  const handleEdit = (puntoVendita: PuntoVendita) => {
    setEditingPuntoVendita(puntoVendita);
    setFormData({
      nome: puntoVendita.nome,
      indirizzo: puntoVendita.indirizzo || '',
      latitudine: puntoVendita.latitudine,
      longitudine: puntoVendita.longitudine,
      costo_vendita_kg: typeof puntoVendita.costo_vendita_kg === 'number' ? puntoVendita.costo_vendita_kg : 30,
      giorno_chiusura: puntoVendita.giorno_chiusura !== undefined ? puntoVendita.giorno_chiusura : null,
      attivo: puntoVendita.attivo !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Elimina punto vendita',
      message: 'Sei sicuro di voler eliminare questo punto vendita?',
      confirmLabel: 'Elimina',
      variant: 'danger'
    });
    if (!ok) return;

    try {
      await apiService.deletePuntoVendita(id);
      setPuntiVendita(prevPuntiVendita =>
        prevPuntiVendita.filter(pv => pv.id !== id)
      );
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore nell\'eliminazione del punto vendita');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      indirizzo: '',
      latitudine: null,
      longitudine: null,
      costo_vendita_kg: 30,
      giorno_chiusura: null,
      attivo: true
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPuntoVendita(null);
    resetForm();
  };

  const handleLocationChange = (location: {
    latitude: number;
    longitude: number;
    address: string;
  }) => {
    setFormData({
      ...formData,
      indirizzo: location.address,
      latitudine: location.latitude,
      longitudine: location.longitude
    });
  };

  const handleGeocoding = async () => {
    if (!formData.indirizzo.trim()) {
      setGeocodingMessage({ text: 'Inserisci un indirizzo prima di cercare', variant: 'error' });
      return;
    }

    try {
      setGeocodingLoading(true);
      setGeocodingMessage(null);

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.indirizzo)}&limit=1&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error('Errore nella ricerca');
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);

        setFormData({
          ...formData,
          latitudine: lat,
          longitudine: lng
        });

        setGeocodingMessage({ text: 'Posizione trovata sulla mappa', variant: 'success' });
      } else {
        setGeocodingMessage({ text: 'Indirizzo non trovato. Prova a essere più specifico.', variant: 'error' });
      }
    } catch (error) {
      console.error('Errore geocoding:', error);
      setGeocodingMessage({ text: 'Errore nella ricerca. Riprova più tardi.', variant: 'error' });
    } finally {
      setGeocodingLoading(false);

      // Nascondi messaggio dopo 3 secondi
      setTimeout(() => {
        setGeocodingMessage(null);
      }, 3000);
    }
  };

  const filteredPuntiVendita = puntiVendita.filter(punto =>
    punto.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (punto.indirizzo && punto.indirizzo.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const isAdmin = user?.ruolo === 'admin';

  if (loading) {
    return (
      <PageContainer>
        <LoadingState />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Gestione Punti Vendita"
        icon={<Store />}
        actions={
          isAdmin && (
            <Button onClick={() => setShowModal(true)}>
              <Plus size={16} />
              Aggiungi Punto Vendita
            </Button>
          )
        }
      />

      {error && <Alert $variant="error">{error}</Alert>}

      <Filters>
        <Input
          type="text"
          placeholder="Cerca punto vendita..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
        />
      </Filters>

      <CardGrid>
        {filteredPuntiVendita.map(punto => (
          <Card key={punto.id}>
            <CardBody>
              <CardTitleRow>
                <h3>{punto.nome}</h3>
                {!punto.attivo && <Badge $variant="error">Inattivo</Badge>}
              </CardTitleRow>

              {punto.indirizzo && (
                <Indirizzo>{punto.indirizzo}</Indirizzo>
              )}

              {/* Widget Meteo */}
              {punto.latitudine && punto.longitudine && (
                <WeatherSection>
                  <WeatherWidget
                    puntoVenditaId={punto.id}
                    compact={true}
                  />
                </WeatherSection>
              )}

              {punto.giorno_chiusura !== null && punto.giorno_chiusura !== undefined && (
                <ClosedDay>
                  <Ban size={14} />
                  Chiuso: {['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'][punto.giorno_chiusura]}
                </ClosedDay>
              )}
              <CreatedDate>
                Creato: {punto.created_at
                  ? new Date(punto.created_at).toLocaleDateString('it-IT')
                  : 'Data non disponibile'
                }
              </CreatedDate>

              {isAdmin && (
                <CardActions>
                  <Button $variant="secondary" $size="sm" onClick={() => handleEdit(punto)}>
                    <Pencil size={14} />
                    Modifica
                  </Button>
                  <Button $variant="danger" $size="sm" onClick={() => handleDelete(punto.id)}>
                    <Trash2 size={14} />
                    Elimina
                  </Button>
                </CardActions>
              )}
            </CardBody>
          </Card>
        ))}
      </CardGrid>

      <Modal
        open={showModal}
        onClose={closeModal}
        title={editingPuntoVendita ? 'Modifica Punto Vendita' : 'Nuovo Punto Vendita'}
        maxWidth="43.75rem"
      >
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Nome *</Label>
            <Input
              type="text"
              required
              value={formData.nome}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, nome: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Costo vendita (€/kg)</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={Number.isFinite(formData.costo_vendita_kg) ? formData.costo_vendita_kg : 30}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                ...formData,
                costo_vendita_kg: e.target.value === '' ? 30 : parseFloat(e.target.value)
              })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Giorno di Chiusura Settimanale</Label>
            <Select
              value={formData.giorno_chiusura !== null && formData.giorno_chiusura !== undefined ? formData.giorno_chiusura : ''}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({
                ...formData,
                giorno_chiusura: e.target.value === '' ? null : parseInt(e.target.value)
              })}
            >
              <option value="">Nessuno (Aperto 7/7)</option>
              <option value="1">Lunedì</option>
              <option value="2">Martedì</option>
              <option value="3">Mercoledì</option>
              <option value="4">Giovedì</option>
              <option value="5">Venerdì</option>
              <option value="6">Sabato</option>
              <option value="0">Domenica</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Indirizzo e Posizione</Label>
            <Textarea
              rows={2}
              value={formData.indirizzo}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, indirizzo: e.target.value })}
              placeholder="Inserisci l'indirizzo completo (es. Via Roma 123, Milano)"
            />

            <GeocodingBox>
              <Button
                type="button"
                $fullWidth
                onClick={handleGeocoding}
                disabled={geocodingLoading || !formData.indirizzo.trim()}
              >
                <Search size={16} />
                {geocodingLoading ? 'Cercando sulla mappa...' : 'Cerca sulla Mappa'}
              </Button>

              {geocodingMessage && (
                <div style={{ marginTop: 'var(--space-sm)' }}>
                  <Alert $variant={geocodingMessage.variant}>
                    {geocodingMessage.text}
                  </Alert>
                </div>
              )}
            </GeocodingBox>
          </FormGroup>

          <MapSelector
            latitude={formData.latitudine}
            longitude={formData.longitudine}
            address={formData.indirizzo}
            onLocationChange={handleLocationChange}
          />

          <FormGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.attivo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, attivo: e.target.checked })}
              />
              Attivo
            </CheckboxLabel>
          </FormGroup>

          <FormActions>
            <Button
              type="button"
              $variant="secondary"
              onClick={closeModal}
            >
              Annulla
            </Button>
            <Button type="submit">
              {editingPuntoVendita ? 'Salva Modifiche' : 'Crea Punto Vendita'}
            </Button>
          </FormActions>
        </form>
      </Modal>
    </PageContainer>
  );
};

export default PuntiVenditaPage;
